import { capitalize, groupBy, map, orderBy, partition } from 'lodash';
import type { ISessionConfig, TSessionTimeSlot } from '~/types/server.types';
import dayjs from '~/common/dayjs';
import { IDayzedMonth } from '@honeybook/hbui/dist/components/HBDayPicker/HBDayPicker.types';
import { SESSION_COLORS } from '~/apps/main/Scheduler/pages/ConfigureSession/utils/configure-session.utils';
import { TDayOfWeek } from '~/types/app.types';
import {
    memberServerToDisplayTimezonesMapping,
    type TShortTimezone
} from '~/utils/timezone.utils';

export const normalizeSessionConfig = ({
    sessionConfig,
    isWithTimezone
}: {
    sessionConfig: ISessionConfig;
    isWithTimezone?: boolean;
}) => {
    const {
        session_color,
        session_type,
        session_duration: { unit_count, unit_type },
        window_type,
        session_timezone
    } = sessionConfig;

    const sessionColor = SESSION_COLORS[session_color];
    const numberOfSharedMembers = sessionConfig.shared_members?.length;
    const sessionDuration = `${unit_count} ${unit_type}`;
    const normalizedSessionType = capitalize(session_type.replaceAll('_', '-'));
    const normalizedWindowType = capitalize(window_type.replaceAll('_', '-'));
    const normalizedSessionWindowAndTimezone = `${normalizedWindowType}`;
    let normalizedSessionTypeAndDuration = `${normalizedSessionType}, ${sessionDuration}`;
    let normalizedDurationAndWindow = `${sessionDuration}, ${normalizedWindowType}`;

    if (isWithTimezone) {
        normalizedSessionTypeAndDuration += `, ${memberServerToDisplayTimezonesMapping[session_timezone]}`;
        normalizedDurationAndWindow += `, ${memberServerToDisplayTimezonesMapping[session_timezone]}`;
    }

    return {
        normalizedDurationAndWindow,
        normalizedSessionType,
        normalizedSessionTypeAndDuration,
        normalizedSessionWindowAndTimezone,
        numberOfSharedMembers,
        sessionColor
    };
};

export const placeholderTimeSlots = (() => {
    let slots: TSessionTimeSlot[] = [];

    const emptySlot: TSessionTimeSlot = {
        startTime: 'hh:mm',
        isDisabled: true
    };

    for (let i = 0; i < 6; i++) {
        slots.push({ ...emptySlot, fakeDaylightType: 'am' });
    }
    for (let i = 0; i < 3; i++) {
        slots.push({ ...emptySlot, fakeDaylightType: 'pm' });
    }

    return slots;
})();

export const getDateFormat = (date: Date | string) =>
    dayjs(date).format('YYYY-MM-DD');

export const getMonthFormat = (date: Date | string) =>
    dayjs(date).format('YYYY-MM');

export const convertDayzedMonthToMonthFormat = (date: IDayzedMonth) => {
    const monthAsDate = dayjs()
        .set('month', date.month)
        .set('year', date.year)
        .toDate();
    return getMonthFormat(monthAsDate);
};

const STORAGE_KEY = 'hb_slot_selected_flow_id';

export const setSlotSelectedInStorage = (flowId: string) =>
    localStorage.setItem(STORAGE_KEY, flowId);

export const getSlotSelectedInStorage = () => localStorage.getItem(STORAGE_KEY);

export const removeSlotSelectedInStorage = () =>
    localStorage.removeItem(STORAGE_KEY);

export const generateNewSessionConfig = (timezone: TShortTimezone) => {
    const indexToDayOfWeek: Record<number, TDayOfWeek> = {
        0: 'Sunday',
        1: 'Monday',
        2: 'Tuesday',
        3: 'Wednesday',
        4: 'Thursday',
        5: 'Friday',
        6: 'Saturday'
    };

    let newSessionDefaults: ISessionConfig = {
        _id: '-1',
        updated_at: null,
        session_name: '',
        session_color: 'purple',
        enabled: true,
        session_description: '',
        session_timezone: timezone,
        session_type: null,
        meeting_location: '',
        phone_call_caller: 'me',
        phone_call_number: '',
        video_meeting_link: '',
        session_duration: {
            unit_type: 'minutes',
            unit_count: 30
        },
        buffer_before: null,
        buffer_after: null,
        notice_period: null,
        window_type: 'indefinitely',
        fixed_start_date: '',
        fixed_end_date: '',
        confirmation_message:
            'Thank you for scheduling a session with me!\n\nPlease view the details of this session below:',
        allow_rescheduling: true,
        allow_canceling: true,
        send_confirmation_email: true,
        schedule_behaviour_type: '',
        day_availabilities: [],
        shared_member_ids: [],
        session_reminders: [
            {
                reminder_type: 'email',
                when: { unit_count: 1, unit_type: 'hours' }
            },
            {
                reminder_type: 'email',
                when: { unit_count: 1, unit_type: 'days' }
            }
        ],
        session_url: undefined,
        shared_members: null,
        placement_id: undefined,
        set_as_project_date: false
    };

    // generate default availabilities
    Object.values(indexToDayOfWeek).forEach(dayOfWeek => {
        newSessionDefaults.day_availabilities.push({
            time_ranges: ['Sunday', 'Saturday'].includes(dayOfWeek)
                ? []
                : [
                      {
                          range_time_start: '9:00',
                          range_time_end: '17:00'
                      }
                  ],
            day_of_week: dayOfWeek,
            override_date: ''
        });
    });

    return newSessionDefaults as ISessionConfig;
};

export function filterDisabledSessions(sessionConfigs: ISessionConfig[]) {
    return sessionConfigs.filter(session => session.enabled);
}

export function partitionSessionByGroupOrIndividual(
    sessionConfigs: ISessionConfig[]
) {
    const [roundRobin, individual] = partition(
        sessionConfigs,
        session => !!session.shared_members
    );
    return { roundRobin, individual };
}

export function groupSessionsForDropdown(
    sessionConfigsOptions: { label: string; value: any; sessionGroup: string }[]
) {
    return orderBy(
        map(
            groupBy(sessionConfigsOptions, 'sessionGroup'),
            (value, sessionGroup) => ({
                label: sessionGroup,
                options: value
            })
        ),
        ['label'],
        ['asc']
    );
}
